import { Empty, Button } from 'antd';
import { UnlinkIcon, RefreshIcon, EditPencilIcon } from "components/Icons";
import TableContainer from 'components/TableContainer'
import _, { orderBy } from 'lodash'
import React from 'react';
import ModalUtil from 'utils/modalUtil';
import { CellRenderers, getFilterProps, Sorters } from 'utils/tableUtils';
import GitInstallationPupop from './GitInstallationPupop';
import { PermissionSetMap } from 'constants/RBACConstants';
import ActionMenuV2 from 'components/ActionMenuV2';
import User from 'components/User';
import AuthorisedContainer from 'components/AuthorisedContainer';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import message from 'components/CustomMessage';
import GitHubSettingsFormPopup from './GitHubSettingsFormPopup';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import { useActiveUserContext } from 'contexts/ActiveUserContext';
import GitHubRepoAndIssuesListing from './GithubRepoAndIssuesListing';

const sortListingData = (data) => {
    if(_.isEmpty(data)) {
        return data
    }
    return orderBy(data, ['createdAt'], ['desc'])
}

export default function InstallationListing({ onUpdateConfigComponent, listingData, name }) {
    const data = sortListingData(listingData);
    const featureFlags = useFeatureFlag();
    const { isArchiveBU } = useActiveUserContext()

    const items = [{
      type: "removeInstallation",
      text: "Remove Installation",
      icon: <UnlinkIcon/>,
      permission: PermissionSetMap.DELETE_TOOL
    },
    ...(featureFlags.SIMPLIFIED_ONBOARDING ? [{
      type: "edit",
      text: "Edit",
      icon: <EditPencilIcon/>,
      permission: PermissionSetMap.EDIT_TOOL_CONFIG
    }] : []
    )];

    const itemsForPAT = [{
        type: "delete",
        text: "Remove Installation",
        icon: <UnlinkIcon/>,
        permission: PermissionSetMap.DELETE_TOOL
      },
      {
        type: "edit",
        text: "Edit",
        icon: <EditPencilIcon/>,
        permission: PermissionSetMap.EDIT_TOOL_CONFIG
      }];

    if(!_.isUndefined(data) && _.isEmpty(data)){
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={"No Installation found."}
            >
            </Empty>
        )
    }

    const handleGithubPATUninstallation = async (id) => {
        try{
            await ToolsIntegrationsAPI.deleteGitInstallations(id).promise;
            message.success("Deleted successfully");
            onUpdateConfigComponent();
        } catch (err) {
            console.error(err);
            throw err;
        }
    }

    const handleUnlinkInstallation = ({hostUrl, installationId, accountType, account, isMoved}) => {
        ModalUtil.show({
            content: <GitInstallationPupop
                        installationId={installationId}
                        hostUrl={hostUrl}
                        accountType={accountType}
                        account={account}
                        unlinkInstallation={isMoved}
                    />,
            afterClose: onUpdateConfigComponent
        })
    }

    const openSetingsPopup = ({ id, installationConfig, hostUrl }) => {
        ModalUtil.show({
            content: <GitHubSettingsFormPopup
                        configID={id}
                        initialValues={installationConfig}
                        hostUrl={hostUrl}
                        toolName={"GitHub"}
                    />,
            afterClose: onUpdateConfigComponent
        })
    }

    const onClick = async (_, record) => {
        switch(record.name){
         case "delete":
            handleGithubPATUninstallation(record.data.id)
            break;    
          case "removeInstallation":
            handleUnlinkInstallation(record.data)
            break;
          case "edit":
            openSetingsPopup(record.data)
            break;
          default:
            break;
        }
    }

    const syncRepos = async() => {
        try {
            const messageReference = message.loading('Updating...')
            await ToolsIntegrationsAPI.syncRepos('GITHUB').promise;
            messageReference();
            message.success("Syncing repos, it may take some time.");
        } catch (e){
            console.log(e);
        }
    }

    const columns = [{
        title: "Id",
        dataIndex: "id",
        sorter: Sorters.NUMBER("id"),
        ...getFilterProps({
            dataIndex: "id",
            dataSource: data,
            onlySearch: true,
            isMultipleValueSupported: true
        }),
        width: 90
    }, {
        title: "Host URL",
        dataIndex: "hostUrl",
        render: CellRenderers.VALUE_OR_NA,
        width: 150,
        ellipsis: true
    }, {
        title: "Username",
        dataIndex: "account",
        render: CellRenderers.VALUE_OR_NA,
        ...getFilterProps({
            dataIndex: "account",
            dataSource: data
        }),
        width: 150,
        ellipsis: true
    },{
        title: "Account Type",
        dataIndex: "accountType",
        render: CellRenderers.VALUE_OR_NA,
        width: 100
    }, 
    {
        title: "Created By",
        dataIndex: "createdBy",
        sorter: Sorters.STRING("createdBy"),
        render: CellRenderers.VALUE_OR_NA,
        width: 200,
        ellipsis: true
    }, {
        title: "Updated By",
        dataIndex: "updatedBy",
        sorter: Sorters.STRING("updatedBy"),
        width: 230,
        render: (text) => <User userEmail={text} />,
        ellipsis: true
    }, {
        title: "Created On",
        dataIndex: "createdAt",
        sorter: Sorters.DATE("createdAt"),
        render: CellRenderers.TIME_RELATIVE,
        width: 150
    }, {
        title: "Updated On",
        dataIndex: "updatedAt",
        sorter: Sorters.DATE("updatedAt"),
        render: CellRenderers.TIME_RELATIVE,
        width: 150
    }, {
        title: "Action",
        key: 'action',
        width: 50,
        render: (record) => {
            return ( 
                <ActionMenuV2 items={ (record?.installationConfig?.authType === "TOKEN") ? itemsForPAT : items} onClick={onClick} data={record} />
            )
        },
    }
];

    return (
        <TableContainer
            enableScroll={true}
            columns={columns}
            dataSource={data}
            loading={_.isUndefined(data)}
            title={"GitHub Installations"}
            defaultSort={{ column: "createdAt", order: "descend" }}
            rowKey={"id"}
            hideExpandAll={true}
            showSizeChanger={true}
            expandable={{
                expandedRowRender: original => <GitHubRepoAndIssuesListing onUpdateConfigComponent={onUpdateConfigComponent} listingData={original} name={name}/>
            }}
            extras={<AuthorisedContainer permission={PermissionSetMap.WRITE_TOOLS} tooltip={isArchiveBU ? "Sync is not supported for Archive BU" : _.isEmpty(data) ? '': "Sync repos"}>
                <Button type='text' size='small' disabled={isArchiveBU ? true : _.isEmpty(data)} onClick={syncRepos}><RefreshIcon /></Button>
            </AuthorisedContainer>}
        />
    )
}